// Permission-gated MODES in the top nav, the breadcrumb and the route — ONE
// map per module (`MODE_GATES`), read three ways. PATTERNS.md BP-90.
//
// WHY THIS EXISTS (Mustang H131, 2026-09-28). navModeRegistry.ts builds each
// module's top-nav `subMenus` once, at import, with no user in sight. A mode
// only some users may open therefore had two bad homes: in MODE_LABELS it
// showed in every pilot's top nav (Crew Tracker, Timesheets Team / Report /
// Codes — Aaron: "Pilot group permissions remove these"); outside it, Flight &
// Duty's ADMIN_MODE_LABELS, it showed in nobody's, so a flight admin could not
// find Rules there (Aaron: "missing the rules mode in teh sub menu"). The
// registry now stamps a named `gate` on such an entry and the menu asks
// `useNavGates()` whether to render it.
//
// H172 (2026-09-29) re-keyed the pilot-facing modes. Keying Crew Tracker and
// Timesheets Team / Report / Codes on 787 hid them from office staff too (787
// holders: crm 0/44, g2 17/82 — Aaron pulled H131 from b10 for it). His ruling:
// "Hide for the Pilots group only". So those modes sit behind
// `notPilotsGroup`; Rules, which WRITES the setting the API gates on 787,
// stays behind `flightAdmin`.
import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { RouteLoading } from "../components/Loading";
import { seesAllPilotsFor } from "../flight-duty/pilotScope";
import { isFlightAdminFor } from "../flight-duty/rules/useIsFlightAdmin";
import { useConfig } from "../hooks/useConfig";
import { useCurrentUser } from "../hooks/useCurrentUser";
import type { SubMenuItem } from "./types";

/**
 * The gates the nav knows. All are resolved in `navGatesFor`, from the one
 * user and the one `flightduty` config `useNavGates` reads.
 *
 *   flightAdmin    — the `flightduty` `view_all_pilots` permission token (787
 *                    on Mustang), exactly as `isFlightAdminFor` derives it and
 *                    as the API's `can_view_all_pilots` / timesheets'
 *                    `_can_view_all_crew` enforce it. Pilots fail it; flight
 *                    admins pass. So do office staff without 787.
 *   notPilotsGroup — shut ONLY for a member of the tenant's Pilots group (F&D's
 *                    crew group, `isInCrewGroupFor` — H74's
 *                    `flightduty_usergroup`) who is not a flight admin. Office
 *                    staff pass whatever they hold; a tenant with no such
 *                    group shuts it for nobody. (H172)
 */
export type NavGate = "flightAdmin" | "notPilotsGroup";

/** Mode segment -> the gate it sits behind. Segments absent are open to all. */
export type ModeGates = { readonly [segment: string]: NavGate };

export interface NavGateState {
  /** Gate -> may this user through? `false` while loading: unknown is NO. */
  open: Record<NavGate, boolean>;
  loading: boolean;
}

const ALL_SHUT: Record<NavGate, boolean> = { flightAdmin: false, notPilotsGroup: false };

/**
 * The pure rule, injectable for tests. `loading` until both the user and the
 * `flightduty` config have resolved (useIsFlightAdmin's rule): every gate
 * reads shut meanwhile, so nothing flashes at a pilot.
 */
export function navGatesFor(
  currentUser: { [key: string]: any } | undefined | null,
  dutyConfig: { [key: string]: any } | undefined | null
): NavGateState {
  if (currentUser == null || dutyConfig === undefined) {
    return { open: ALL_SHUT, loading: true };
  }
  const flightAdmin = isFlightAdminFor(currentUser, dutyConfig);
  return {
    open: {
      flightAdmin,
      // The one "sees every pilot" rule (pilotScope.ts, H178) — the server's
      // sees_all_records, which the data behind these modes reads.
      notPilotsGroup: seesAllPilotsFor(currentUser, dutyConfig),
    },
    loading: false,
  };
}

export function useNavGates(): NavGateState {
  const currentUser = useCurrentUser();
  const dutyConfig = useConfig("flightduty");
  return useMemo(() => navGatesFor(currentUser, dutyConfig), [currentUser, dutyConfig]);
}

const own = (map: object, key: string) =>
  Object.prototype.hasOwnProperty.call(map, key);

/** A module's label map as nav `subMenus`, each gated entry stamped with its gate. */
export function modeSubMenus(
  labels: { [segment: string]: string },
  gates: ModeGates = {}
): SubMenuItem[] {
  return Object.entries(labels).map(([segment, label]) =>
    own(gates, segment)
      ? { name: label, to: segment, gate: gates[segment] }
      : { name: label, to: segment }
  );
}

/** The entries this user may see: ungated ones, and gated ones whose gate is open. */
export function openSubMenus(
  items: ReadonlyArray<SubMenuItem>,
  open: Record<NavGate, boolean>
): SubMenuItem[] {
  return items.filter((item) => !item.gate || open[item.gate] === true);
}

/** A layout's breadcrumb rows: `[segment, label]` for every mode this user may open. */
export function visibleModes(
  labels: { [segment: string]: string },
  gates: ModeGates,
  open: Record<NavGate, boolean>
): Array<[string, string]> {
  return Object.entries(labels).filter(
    ([segment]) => !own(gates, segment) || open[gates[segment]] === true
  );
}

/**
 * The ROUTE gate on a gated mode. The menus only hide it; a pilot can still
 * type the URL or restore it from `<slug>.viewMode`. While the gate resolves
 * this shows the route spinner (never the page); a user it keeps out is sent
 * to `redirectTo(pathname)` with `replace`, so Back does not bounce them into
 * the redirect again. The API enforces the same rule on the data.
 */
export function ModeGate({
  gate,
  redirectTo,
  children,
}: {
  gate: NavGate;
  redirectTo: (pathname: string) => string;
  children: React.ReactNode;
}) {
  const { open, loading } = useNavGates();
  const location = useLocation();
  if (loading) return <RouteLoading />;
  if (!open[gate]) return <Navigate to={redirectTo(location.pathname)} replace />;
  return <>{children}</>;
}
